/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

export const formatDate = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dStr = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dStr}`;
};

export function formatDisplayDate(dateOrStr: string | Date | undefined): string {
  if (!dateOrStr) return '';
  try {
    const d = dateOrStr instanceof Date ? dateOrStr : parseDate(dateOrStr);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}.${month}.${year}`;
  } catch (e) {
    return String(dateOrStr);
  }
}

export const todayStr = formatDate(new Date());

const dateCache = new Map<string, Date>();

export function parseDate(str?: string | null): Date {
  const safeToday = () => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return now;
  };

  if (!str || typeof str !== 'string' || !str.trim()) {
    return safeToday();
  }

  const trimmed = str.trim();
  if (dateCache.has(trimmed)) {
    return new Date(dateCache.get(trimmed)!.getTime());
  }

  const cleanStr = trimmed.split('T')[0].split(' ')[0];
  let date: Date;
  if (cleanStr.includes('-')) {
    const parts = cleanStr.split('-');
    const y = Number(parts[0]);
    const m = Number(parts[1]);
    const d = Number(parts[2]);
    date = new Date(y, m - 1, d);
  } else if (trimmed.includes('.')) {
    const parts = trimmed.split('.');
    const d = Number(parts[0]);
    const m = Number(parts[1]);
    let y = parts[2] !== undefined ? Number(parts[2]) : new Date().getFullYear();

    if (parts[2] !== undefined) {
      if (y < 100) y += 2000;
    }

    date = new Date(y, m - 1, d);
    if (y < 100) date.setFullYear(y);
  } else {
    date = safeToday();
  }

  if (Number.isNaN(date.getTime())) {
    date = safeToday();
  } else {
    const year = date.getFullYear();
    if (year < 1900) date.setFullYear(1900);
    if (year > 2100) date.setFullYear(2100);
    date.setHours(0, 0, 0, 0);
  }

  if (dateCache.size > 1000) dateCache.clear();
  dateCache.set(trimmed, date);
  return new Date(date.getTime());
}

export const addDays = (str: string, days: number): string => {
  const d = parseDate(str);
  d.setDate(d.getDate() + days);
  return formatDate(d);
};

export const diffDays = (str1?: string, str2?: string): number => {
  if (!str1 || !str2) return 0;
  const d1 = parseDate(str1);
  const d2 = parseDate(str2);
  return Math.round((d2.getTime() - d1.getTime()) / (1000 * 60 * 60 * 24));
};

export const normalizeImportDate = (dateStr: string): string => {
  if (!dateStr) return "";
  dateStr = dateStr.trim();
  if (dateStr.includes('.')) {
    const parts = dateStr.split('.');
    if (parts.length === 3) {
      let [d, m, y] = parts;
      if (y.length === 2) y = `20${y}`;
      return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
    }
    if (parts.length === 2) {
      const [d, m] = parts;
      const y = new Date().getFullYear();
      return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
    }
  }
  return dateStr;
};

export const getTaskDateBucket = (task: { start?: string, end?: string, completed?: boolean, status?: string }, today: string): string => {
  const statusLower = (task.status || '').toLowerCase();
  const isCompleted = task.completed || 
                      statusLower === 'completed' || 
                      statusLower === 'done' || 
                      statusLower === 'erledigt';
  if (isCompleted) return 'completed';
  
  const isHold = statusLower === 'hold' || 
                 statusLower === 'waiting' || 
                 statusLower === 'wartend' || 
                 statusLower === 'blocked' ||
                 statusLower === 'blockiert';
  if (isHold) return 'hold';
  if (task.end && diffDays(today, task.end) < 0) return 'overdue';
  
  // 2. Today: Task is active (started and not finished) OR starts today
  const startDiff = task.start ? diffDays(today, task.start) : (task.end ? diffDays(today, task.end) : 999);
  const endDiff = task.end ? diffDays(today, task.end) : 999;
  
  if (startDiff <= 0 && endDiff >= 0) return 'today';
  
  // 3. Week: Starts within the next 7 days
  if (startDiff > 0 && startDiff <= 7) return 'week';
  
  // 4. Month: Starts within the next 30 days
  if (startDiff > 7 && startDiff <= 30) return 'month';
  
  // 5. Longterm: Starts in more than 30 days or no date info
  return 'longterm';
};

export function formatInboxItemDate(item: { date?: string; createdAt?: number; id?: string } | null | undefined): string {
  if (!item) return '';

  let timestamp: number | null = item.createdAt || null;

  // If no createdAt field, try to extract timestamp from item ID (e.g., voice_1779120798570, chat_1779120798570)
  if (!timestamp && item.id) {
    const match = item.id.match(/\d{12,14}/);
    if (match) {
      timestamp = parseInt(match[0], 10);
    }
  }

  if (timestamp && !isNaN(timestamp)) {
    const itemDate = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const isSameDay = (d1: Date, d2: Date) => 
      d1.getDate() === d2.getDate() && 
      d1.getMonth() === d2.getMonth() && 
      d1.getFullYear() === d2.getFullYear();

    const timeStr = itemDate.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });

    if (isSameDay(itemDate, today)) {
      return `Heute, ${timeStr}`;
    }
    if (isSameDay(itemDate, yesterday)) {
      return `Gestern, ${timeStr}`;
    }
    const day = String(itemDate.getDate()).padStart(2, '0');
    const month = String(itemDate.getMonth() + 1).padStart(2, '0');
    const year = itemDate.getFullYear();
    return `${day}.${month}.${year}`;
  }

  // Fallback: If date exists and is not 'Heute', return date
  if (item.date && item.date !== 'Heute') {
    return item.date;
  }

  return 'Eingegangen';
}
